import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNotify } from 'react-admin';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import Step from '@mui/material/Step';
import StepLabel from '@mui/material/StepLabel';
import Stepper from '@mui/material/Stepper';
import Typography from '@mui/material/Typography';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';

import { getCurrentPartner } from '@src/providers/dataProvider/shared/partner';
import type { CatalogTemplateDefinitions } from '@src/settings';

import {
  INITIAL_WIZARD_STATE,
  TROVE_SKU_KEY,
  buildFieldCatalog,
  getAllSourceColumns,
  getRequiredKeys,
  getWizardSteps,
  hasAnyConditionSet,
  withTroveSkuField,
  withoutVitalElementFields,
} from './types';
import type { WizardState } from './types';
import { fetchCatalogItemsForTemplate } from './fetchCatalogItemsForTemplate';
import type { CatalogTemplateItemRow } from './fetchCatalogItemsForTemplate';
import { fetchPartnerVitalElementColumns } from './fetchPartnerVitalElementColumns';
import { generateTemplateCsv } from './generateTemplateCsv';
import ScopeStep from './ScopeStep';
import FieldsToUpdateStep from './FieldsToUpdateStep';
import ValuesStep from './ValuesStep';
import ReviewStep from './ReviewStep';

interface CreateCatalogUploadTemplateDialogProps {
  open: boolean;
  onClose: () => void;
  /** Partner's accepted catalog upload columns (`settings.catalogTemplateDefinitions`). */
  templateDefinitions?: CatalogTemplateDefinitions | null;
}

// The default purpose is "create", so a fresh/reset state needs its required fields/identifiers
// pre-selected immediately — not left for the purpose-sync effect to backfill on a later render,
// which would leave a window (until that effect's next run, e.g. once an unrelated vital-elements
// fetch resolves) where Fields to Update shows required fields checked but `selectedFields` is
// actually empty, disabling Next. Only "create" needs seeding here: this always runs with the
// default purpose, and "update" gets its forced Trove SKU field from the purpose-sync effect once
// the partner actually switches to it.
const buildInitialState = (definitions: CatalogTemplateDefinitions | null | undefined): WizardState => {
  const catalog = buildFieldCatalog(definitions);
  return {
    ...INITIAL_WIZARD_STATE,
    selectedFields: getRequiredKeys(catalog.fields),
    selectedIdentifiers: getRequiredKeys(catalog.identifiers),
  };
};

const CreateCatalogUploadTemplateDialog = ({
  open,
  onClose,
  templateDefinitions,
}: CreateCatalogUploadTemplateDialogProps) => {
  const notify = useNotify();
  const partner = getCurrentPartner();

  const [state, setState] = useState<WizardState>(() => buildInitialState(templateDefinitions));
  const [vitalElementColumns, setVitalElementColumns] = useState<string[]>([]);

  // Best-effort — partners without the `admin_vital_elements_portal` permission (or a backend
  // that errors) just see every field; nothing gets excluded.
  useEffect(() => {
    if (!open || !partner?.code) return undefined;
    let cancelled = false;
    fetchPartnerVitalElementColumns(partner.code)
      .then((columns) => {
        if (!cancelled) setVitalElementColumns(columns);
      })
      .catch((error) => {
        if (cancelled) return;
        console.error('CreateCatalogUploadTemplateDialog: failed to fetch vital elements', {
          error: error instanceof Error ? error.message : error,
        });
        setVitalElementColumns([]);
      });
    return () => {
      cancelled = true;
    };
  }, [open, partner?.code]);

  // The upload validator requires a `sku` column to match rows to existing records on an
  // "update" template — surfaced as a locked "Trove SKU" field. A "create new records" template
  // has no existing sku to match, so that field doesn't apply and is left out entirely. "Update"
  // also drops the partner's vital-element columns (e.g. parent/color/size SKU components) from
  // Fields to Update — they identify the record rather than being updatable data; "create" keeps
  // them, since there they're ordinary (often required) columns like any other.
  const fieldCatalog = useMemo(() => {
    const base = buildFieldCatalog(templateDefinitions);
    if (state.purpose === 'create') return base;
    return withoutVitalElementFields(withTroveSkuField(base), vitalElementColumns);
  }, [templateDefinitions, state.purpose, vitalElementColumns]);
  const sourceColumns = useMemo(() => getAllSourceColumns(fieldCatalog), [fieldCatalog]);

  const [activeStep, setActiveStep] = useState(0);
  const [catalogItems, setCatalogItems] = useState<CatalogTemplateItemRow[]>([]);
  const [matchedCount, setMatchedCount] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const wizardSteps = useMemo(() => getWizardSteps(state.purpose), [state.purpose]);
  const reviewStepIndex = wizardSteps.length - 1;

  const updateState = useCallback((patch: Partial<WizardState>) => {
    setState(prev => ({ ...prev, ...patch }));
  }, []);

  // Keep selections in sync with what the chosen purpose always requires. A "create new records"
  // template has nothing to match against, so the partner's `required` columns — whether regular
  // fields or identifiers like UPC/EAN — must be filled in directly (pre-checked + locked in
  // FieldsToUpdateStep). An "update" template matches rows by Trove SKU instead, so that's the
  // only forced field there, and required identifiers stay an optional choice on the Values step.
  const previousPurposeRef = useRef(state.purpose);
  useEffect(() => {
    const purposeChanged = previousPurposeRef.current !== state.purpose;
    previousPurposeRef.current = state.purpose;

    // Switching purpose changes which fields are even offered, and why — a selection made under
    // the old purpose (e.g. the required fields auto-checked while "create" was active) isn't
    // meaningful under the new one, so start over with just what's forced rather than merging
    // onto leftovers (otherwise e.g. switching from "create" to "update" would leave every
    // required field sitting pre-checked, but no longer locked, in Fields to Update).
    if (purposeChanged) {
      updateState({
        selectedFields: state.purpose === 'create' ? getRequiredKeys(fieldCatalog.fields) : [TROVE_SKU_KEY],
        selectedIdentifiers: state.purpose === 'create' ? getRequiredKeys(fieldCatalog.identifiers) : [],
      });
      return;
    }

    if (state.purpose === 'create') {
      const requiredFieldKeys = getRequiredKeys(fieldCatalog.fields);
      const requiredIdentifierKeys = getRequiredKeys(fieldCatalog.identifiers);
      const missingFields = requiredFieldKeys.filter(key => !state.selectedFields.includes(key));
      const missingIdentifiers = requiredIdentifierKeys.filter(key => !state.selectedIdentifiers.includes(key));
      if (missingFields.length === 0 && missingIdentifiers.length === 0) return;
      updateState({
        selectedFields: [...state.selectedFields, ...missingFields],
        selectedIdentifiers: [...state.selectedIdentifiers, ...missingIdentifiers],
      });
    } else if (!state.selectedFields.includes(TROVE_SKU_KEY)) {
      updateState({ selectedFields: [TROVE_SKU_KEY, ...state.selectedFields] });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.purpose, fieldCatalog]);

  // Re-seed whenever the dialog opens. Deliberately scoped to `[open]` only (not
  // `templateDefinitions`) — this should reset on an open/close cycle, not merely because the
  // partner's schema prop happened to update while the dialog was already open.
  useEffect(() => {
    if (!open) return;
    setState(buildInitialState(templateDefinitions));
    setActiveStep(0);
    setCatalogItems([]);
    setMatchedCount(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const activeStepKey = wizardSteps[activeStep]?.key;

  const handleClose = useCallback(() => {
    setState(buildInitialState(templateDefinitions));
    setActiveStep(0);
    setCatalogItems([]);
    setMatchedCount(null);
    setIsLoading(false);
    onClose();
  }, [onClose, templateDefinitions]);

  const isStepValid = useMemo(() => {
    switch (activeStepKey) {
      case 'scope':
        return state.scopeMode !== 'subset' || hasAnyConditionSet(state);
      case 'fields':
        return state.selectedFields.length > 0 || state.customFields.some(c => c.key.trim());
      case 'values':
        return true;
      default:
        return true;
    }
  }, [activeStepKey, state]);

  const fetchGeneration = useRef(0);

  const fetchCatalogData = useCallback(async () => {
    const generation = ++fetchGeneration.current;
    setIsLoading(true);
    try {
      const result = await fetchCatalogItemsForTemplate(state, sourceColumns);
      if (generation !== fetchGeneration.current) return; // stale fetch
      setCatalogItems(result.rows);
      setMatchedCount(result.count);
    } catch (error) {
      if (generation !== fetchGeneration.current) return; // stale fetch
      console.error('CreateCatalogUploadTemplateDialog: failed to fetch catalog items', {
        error: error instanceof Error ? error.message : error,
      });
      const message = error instanceof Error ? error.message : 'Failed to load catalog items';
      notify(`Error: ${message}`, { type: 'error' });
    } finally {
      if (generation === fetchGeneration.current) {
        setIsLoading(false);
      }
    }
  }, [state, sourceColumns, notify]);

  const handleNext = useCallback(async () => {
    if (activeStepKey === 'scope') {
      setActiveStep(prev => prev + 1);
      // A "create new records" template has nothing to fetch — there's no existing data to
      // scope against or pre-fill from, so Review just shows the header row.
      if (state.purpose === 'update') {
        await fetchCatalogData();
      }
    } else {
      setActiveStep(prev => prev + 1);
    }
  }, [activeStepKey, fetchCatalogData, state.purpose]);

  const handleBack = useCallback(() => {
    if (wizardSteps[activeStep - 1]?.key === 'scope') {
      setCatalogItems([]); // scope may change — invalidate fetched data
      setMatchedCount(null);
    }
    setActiveStep(prev => prev - 1);
  }, [activeStep, wizardSteps]);

  const generateFilename = useCallback((): string => {
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
    return `${partner?.code}-catalogUploadTemplate-${timestamp}.csv`;
  }, [partner?.code]);

  const handleDownload = useCallback(() => {
    const csv = generateTemplateCsv(catalogItems, state, fieldCatalog);
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = generateFilename();
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    notify('Template downloaded', { type: 'success' });
    handleClose();
  }, [catalogItems, state, fieldCatalog, notify, handleClose, generateFilename]);

  return (
    <Dialog open={open} onClose={handleClose} maxWidth={false} fullWidth sx={{ '& .MuiDialog-paper': { maxWidth: 975 } }}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box>
          <Typography variant="h6" component="span">Create Catalog Upload Template</Typography>
          {partner?.name && (
            <Typography variant="body2" color="text.secondary">{partner.name}</Typography>
          )}
        </Box>
        <IconButton size="small" onClick={handleClose} aria-label="close">
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <Stepper activeStep={activeStep} sx={{ pt: 1, pb: 3 }}>
          {wizardSteps.map(step => (
            <Step key={step.key}>
              <StepLabel>{step.label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {activeStepKey === 'scope' && (
          <ScopeStep state={state} updateState={updateState} />
        )}
        {activeStepKey === 'fields' && (
          <FieldsToUpdateStep
            state={state}
            updateState={updateState}
            matchedCount={matchedCount}
            fields={fieldCatalog.fields}
            identifiers={fieldCatalog.identifiers}
            allowCustomFields={templateDefinitions?.additionalProperties === true}
          />
        )}
        {activeStepKey === 'values' && (
          <ValuesStep state={state} updateState={updateState} identifiers={fieldCatalog.identifiers} />
        )}
        {activeStepKey === 'review' && (
          <ReviewStep state={state} catalogItems={catalogItems} isLoading={isLoading} catalog={fieldCatalog} />
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 1.5 }}>
        <Button onClick={handleClose} sx={{ mr: 'auto' }}>Cancel</Button>
        {activeStep > 0 && (
          <Button onClick={handleBack}>Back</Button>
        )}
        {activeStep < reviewStepIndex ? (
          <Button variant="contained" onClick={handleNext} disabled={!isStepValid}>
            Next
          </Button>
        ) : (
          <Button
            variant="contained"
            onClick={handleDownload}
            disabled={isLoading || (state.purpose === 'update' && catalogItems.length === 0)}
            startIcon={<DownloadIcon />}
          >
            Download Template
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default CreateCatalogUploadTemplateDialog;
